import { type Observable } from 'rxjs';

import { type CanvasNamespaceId, type DialogClosePayload } from '@blaze-runtime/engine-domain';
import { type PageContext } from '@blaze-shared/projects-domain';

/**
 * Puts a page in front of the user as a dialog or a drawer, and waits for it to close.
 *
 * A navigate node targeting a dialog-like page suspends the workflow until the dialog closes: the
 * payload it closes with decides whether the workflow continues and with what data. Building the
 * overlay is the engine's business.
 */
export abstract class EngineDialogPages {
  /**
   * Opens the page as an overlay.
   * @returns the payload the dialog closes with — `undefined` when it was dismissed.
   */
  abstract openDialog(
    dialogNamespaceId: CanvasNamespaceId,
    pageContext: PageContext | undefined,
    isDrawer?: boolean,
  ): Observable<DialogClosePayload>;

  /**
   * Dismisses the open overlay, resolving the {@link openDialog} that opened it.
   * @param payload the value the dialog closes with; omitted when the node declares no output.
   */
  abstract closeDialog(dialogNamespaceId: CanvasNamespaceId, payload?: unknown): void;
}
